// Devise courante du commerce connecté. Mise à jour par AuthContext dès que
// le commerce est chargé (setDeviseCourante). Repli : 'GNF'. Comme la devise
// ne change qu'à la connexion / dans les paramètres, une variable de module
// suffit — pas besoin de la passer à chaque appel de formatMontant.
let deviseCourante = 'GNF';
export const setDeviseCourante = (d) => { deviseCourante = d || 'GNF'; };
export const getDeviseCourante = () => deviseCourante;

// Formatage d'un montant, suffixé par la devise du commerce.
export const formatMontant = (montant) => {
  if (montant === null || montant === undefined) return `0 ${deviseCourante}`;
  return new Intl.NumberFormat('fr-GN', {
    style: 'decimal',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(montant) + ' ' + deviseCourante;
};

// Formatage date
export const formatDate = (date) => {
  if (!date) return '';
  return new Intl.DateTimeFormat('fr-FR', {
    day: '2-digit', month: '2-digit', year: 'numeric'
  }).format(new Date(date));
};

// Formatage date + heure
export const formatDateHeure = (date) => {
  if (!date) return '';
  return new Intl.DateTimeFormat('fr-FR', {
    day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit'
  }).format(new Date(date));
};

// Date du jour au format YYYY-MM-DD
export const aujourdhui = () => new Date().toISOString().split('T')[0];

// Couleur selon le statut commande
export const couleurStatut = (statut) => {
  const couleurs = {
    en_attente: 'bg-yellow-100 text-yellow-800',
    confirmee: 'bg-blue-100 text-blue-800',
    en_livraison: 'bg-orange-100 text-orange-800',
    livree: 'bg-green-100 text-green-800',
    annulee: 'bg-red-100 text-red-800',
  };
  return couleurs[statut] || 'bg-gray-100 text-gray-800';
};

// Couleur niveau de stock
export const couleurStock = (quantite, seuil) => {
  if (quantite === 0) return 'text-red-600 font-bold';
  if (quantite <= seuil) return 'text-orange-500 font-semibold';
  return 'text-green-600';
};

// Nettoie un libellé « a · b · a » en retirant les parties répétées
// (séquelle d'un renommage de caractéristique qui doublait les valeurs,
// ex. « 8 mm · 8 mm » → « 8 mm »). Conserve l'ordre, dédoublonne sur la valeur.
export const nettoyerLibelle = (str) => {
  if (!str) return '';
  const parts = String(str).split('·').map(p => p.trim()).filter(Boolean);
  return [...new Set(parts)].join(' · ');
};

// Libellé lisible d'une variante.
// Modèle 049 : `modele` contient déjà le libellé complet (« 128 Go · Noir »).
// Pour les anciennes données séparées, on reconstruit sans doublon.
// Renvoie '' pour un produit simple (aucune caractéristique).
export const libelleVariante = (v) => {
  if (!v) return '';
  if (v.attributs && typeof v.attributs === 'object' && Object.keys(v.attributs).length > 0) {
    // Dédoublonnage : un même valeur d'attribut ne doit pas apparaître 2 fois.
    const vals = [...new Set(Object.values(v.attributs).filter(Boolean))];
    return vals.join(' · ');
  }
  const taille = v.taille || v.taille_nom;
  const parts = [v.modele, taille, v.couleur].filter(Boolean);
  return nettoyerLibelle([...new Set(parts)].join(' · '));
};

// Répartit un total EXACT sur des lignes pour que la somme des sous-totaux
// AFFICHÉS égale ce total au franc près. Corrige le décalage d'arrondi des
// prix unitaires issus d'une décomposition de conditionnement (ex. tonne =
// 56 barres : 1 200 000 / 56 = 21 428,57 → 56 × 21 429 = 1 200 024 ≠ total).
// N'altère AUCUNE donnée stockée — usage purement affichage (bons imprimés).
// Renvoie chaque ligne enrichie de { total_affiche, pu_affiche }.
export const repartirTotalSurLignes = (lignes, totalExact) => {
  const arr = Array.isArray(lignes) ? lignes : [];
  const raws = arr.map((l) =>
    Number(l.sous_total != null ? l.sous_total
      : Number(l.quantite || 0) * Number(l.prix_achat_unitaire || 0))
  );
  const sumRaw = raws.reduce((s, v) => s + v, 0);
  const total = Number(totalExact);
  // Sans total fiable ou somme nulle : valeurs inchangées.
  if (!Number.isFinite(total) || total <= 0 || sumRaw <= 0) {
    return arr.map((l, i) => ({
      ...l, total_affiche: raws[i], pu_affiche: Number(l.prix_achat_unitaire || 0),
    }));
  }
  let alloue = 0;
  return arr.map((l, i) => {
    const qte = Number(l.quantite || 0);
    const last = i === arr.length - 1;
    // Le dernier absorbe le reste → la somme des lignes = total EXACT.
    const totalLigne = last ? total - alloue : Math.round((total * raws[i]) / sumRaw);
    if (!last) alloue += totalLigne;
    return { ...l, total_affiche: totalLigne, pu_affiche: qte > 0 ? totalLigne / qte : 0 };
  });
};

// Formatte un prix unitaire : entier si rond, sinon 2 décimales (fr-FR).
export const formatPU = (n) => {
  const v = Number(n || 0);
  const dec = Math.abs(v - Math.round(v)) < 0.005 ? 0 : 2;
  return v.toLocaleString('fr-FR', { minimumFractionDigits: dec, maximumFractionDigits: dec });
};

// Générer un code variante unique
export const genererCodeVariante = (categorie, modele, taille, couleur) => {
  const clean = (str) => str.toUpperCase().replace(/\s+/g, '').substring(0, 3);
  return `${clean(categorie)}-${clean(modele)}-${clean(taille)}-${clean(couleur)}-${Date.now().toString(36).toUpperCase()}`;
};
